import React, { useState, useRef, useEffect } from "react";
import { US_STATES } from "@/constants/usStates";

interface State {
    name: string;
    abbreviation: string;
}

interface StateDropdownProps {
    value: string;
    onChange: (value: string) => void;
    error?: string;
    placeholder?: string;
    id?: string;
}

export const StateDropdown: React.FC<StateDropdownProps> = ({
    value,
    onChange,
    error,
    placeholder = "Select State",
    id = "state",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [localError, setLocalError] = useState<string | null>(null);

    const filteredStates = US_STATES.filter(state =>
        state.name.toLowerCase().startsWith(searchTerm.toLowerCase()) ||
        state.abbreviation.toLowerCase().startsWith(searchTerm.toLowerCase())
    );

    const selectedState = US_STATES.find(state => state.abbreviation === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm("");
                setFocusedIndex(-1);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                e.preventDefault();
                setIsOpen(true);
                setFocusedIndex(0);
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setFocusedIndex(prev =>
                    prev < filteredStates.length - 1 ? prev + 1 : 0
                );
                break;
            case "ArrowUp":
                e.preventDefault();
                setFocusedIndex(prev =>
                    prev > 0 ? prev - 1 : filteredStates.length - 1
                );
                break;
            case "Enter":
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredStates.length) {
                    handleSelect(filteredStates[focusedIndex]);
                }
                break;
            case "Escape":
                setIsOpen(false);
                setSearchTerm("");
                setFocusedIndex(-1);
                inputRef.current?.blur();
                break;
        }
    };

    const handleSelect = (state: State) => {
        onChange(state.abbreviation);
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;

        if (val.length > 30) {
            setLocalError("State name must be less than 30 characters.");
            return;
        }

        setLocalError(null);
        setSearchTerm(val);
        setFocusedIndex(-1);
        if (!isOpen) {
            setIsOpen(true);
        }
    };

    const handleToggle = () => {
        if (isOpen) {
            setIsOpen(false);
            setSearchTerm("");
            setFocusedIndex(-1);
        } else {
            setIsOpen(true);
            inputRef.current?.focus();
        }
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <div className="relative">
                <div
                    className={`relative flex items-center rounded-md bg-white cursor-pointer transition-colors ${error
                        ? "border-red-500 focus-within:ring-2 focus-within:ring-red-200"
                        : "focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200"
                        }`}
                    onClick={handleToggle}
                >
                    <input
                        ref={inputRef}
                        id={id}
                        type="text"
                        value={isOpen ? searchTerm : (selectedState ? selectedState.name : "")}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className="w-full appearance-none bg-white border border-white text-[var(--color-gray-900)] 
    py-4.5 px-7 pr-14 rounded text-base 
    bg-[url('data:image/svg+xml,%3Csvg width=...')] bg-no-repeat bg-[length:32px] bg-[right_20px_center]
    focus:outline-none 
    focus:border-[#8c8d90]
    focus:ring-4 
    focus:ring-[rgba(24,26,32,0.25)]"
                        autoComplete="off"
                        readOnly={!isOpen}
                        maxLength={30}
                    />

                    <div className="absolute right-3 pointer-events-none">
                        <i
                            className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} text-gray-400 text-lg`}
                        />
                    </div>
                </div>

                {(error || localError) && (
                    <p className="mt-1 text-sm text-red-600">
                        {localError || error}
                    </p>
                )}

            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredStates.length > 0 ? (
                        <div className="py-1">
                            {filteredStates.map((state, index) => (
                                <div
                                    key={state.abbreviation}
                                    className={`px-4 py-2 cursor-pointer transition-colors ${index === focusedIndex
                                        ? "bg-blue-50 text-blue-900"
                                        : "hover:bg-gray-50"
                                        } ${state.abbreviation === value ? "bg-blue-100 text-blue-900" : ""
                                        }`}
                                    onClick={() => handleSelect(state)}
                                    onMouseEnter={() => setFocusedIndex(index)}
                                >
                                    <div className="flex justify-between items-center">
                                        <span>{state.name}</span>
                                        <span className="text-gray-500 text-sm">{state.abbreviation}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                            No states found matching "{searchTerm}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
